"use client";

import {
  Cross2Icon,
  HamburgerMenuIcon,
  TriangleDownIcon,
  ChevronRightIcon,
} from "@radix-ui/react-icons";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { getQueryParamsFromUrl } from "@/components/common/utils";
import { Categories, PinnedPage } from "@/interface/types";
import axios from "axios";
import { useOptimizedScrollHandler } from "@/utils/performance/scrollOptimizations";

interface UpdatedHeaderProps {
  twclass?: string;
}

const CmsHeader: React.FC<UpdatedHeaderProps> = ({ twclass }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [categories, setCategories] = useState<Categories[]>([]);
  const [pinnedPages, setPinnedPages] = useState<PinnedPage[]>([]);
  const [hoveredCategory, setHoveredCategory] = useState("");
  const [pinnedPagesByCategory, setPinnedPagesByCategory] = useState<{
    [key: string]: PinnedPage[];
  }>({});

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.style.overflow = !isMenuOpen ? "hidden" : "unset";
  };

  function handleResize() {
    if (window.innerWidth < 768) {
      !isMobile && setIsMobile(true);
    } else {
      isMobile && setIsMobile(false);
    }
  }

  async function getUserIp() {
    try {
      const res = await fetch("https://api.ipify.org/?format=json");
      return res.json();
    } catch {
      console.error("ip address getting is an issue with api");
    }
  }

  // Use optimized scroll handler for header fixed state
  const handleHeaderScroll = () => {
    if (window.scrollY > 0) {
      setIsHeaderFixed(true);
    } else {
      setIsHeaderFixed(false);
    }
  };

  useOptimizedScrollHandler(handleHeaderScroll, [isHeaderFixed], {
    throttleMs: 50,
  });

  useEffect(() => {
    getQueryParamsFromUrl();
    document.body.style.overflow = "unset";
    (async () => {
      try {
        const res = await getUserIp();
        localStorage.setItem("ip", res.ip);
      } catch (e) {
        console.error("error-", e);
      }
    })();

    window.addEventListener("resize", handleResize);
    handleResize();
    window.scroll({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const handleCategoryClick = (categorySlug: string) => {
    setActiveCategory((prev) => (prev === categorySlug ? "" : categorySlug));
    fetchPinnedPages(categorySlug);
  };

  const getCategories = async () => {
    try {
      const API_URL =
        "https://website-cms-backend-prod-154625965269.asia-south1.run.app/categories/?vertical=Accounting";
      const response = await axios.get(API_URL);
      const { status, data } = response;
      if (status === 200) {
        setCategories(data.Data);
      } else {
        console.warn("empty array");
      }
    } catch (error) {
      console.error("failed to fetch categories");
    }
  };

  const fetchPinnedPages = async (categorySlug: string) => {
    try {
      const API_URL = `https://website-cms-backend-prod-154625965269.asia-south1.run.app/pinned_pages/?category=${categorySlug}`;
      const response = await axios.get(API_URL);
      const { status, data } = response;
      if (status === 200) {
        setPinnedPages(data.Data);
        setPinnedPagesByCategory((prev) => ({
          ...prev,
          [categorySlug]: data.Data,
        }));
      } else {
        console.warn("Error while fetching pinned pages list...");
      }
    } catch (error) {
      console.error("Failed to fetch pinned pages list");
    }
  };

  useEffect(() => {
    if (activeCategory && !pinnedPagesByCategory[activeCategory]) {
      fetchPinnedPages(activeCategory);
    }
    getCategories();
  }, [activeCategory]);

  const handleScrollToCategory = (categoryName: string) => {
    const element = document.getElementById(`category-${categoryName}`);
    if (element) {
      const offset = 150;
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleCategoryHover = async (categoryName: string) => {
    setHoveredCategory(categoryName);
    setPinnedPages([]);
    await fetchPinnedPages(categoryName);
  };

  return (
    <>
      <header>
        <div
          className={`${
            isHeaderFixed
              ? "fixed top-0 left-0 right-0 bg-[#191919] z-[999]"
              : "relative bg-[#191919] z-[999]"
          } lg:py-5 py-4 px-0 sm:px-5 px_5 lg:px-0 flex justify-between items-center md:flex-nowrap md:justify-between flex-wrap ${
            twclass ? twclass : "menu_shadow"
          }`}
        >
          <div className="main_container flex justify-between items-center md:flex-nowrap flex-wrap w-full z-50 poppins_medium">
            <>
              {isMobile ? (
                <Link href="/">
                  <Image
                    src="https://ik.imagekit.io/mileseducation/miles_website/home/new_logo.webp"
                    alt="Special Offer"
                    className="dark:invert duration-300 w-[120px] ml-1"
                    width={133}
                    priority
                    height={24}
                  />
                </Link>
              ) : (
                <Link href="/">
                  <Image
                    src="https://ik.imagekit.io/mileseducation/miles_website/home/new_logo.webp"
                    alt="Special Offer"
                    className="dark:invert duration-300 xl:w-[133px] lg:w-[113px] md:w-[120px] w-[120px] sm:ml-0 ml-4"
                    width={133}
                    priority
                    height={38}
                  />
                </Link>
              )}
            </>
            {isMobile ? (
              <div className="flex items-center gap-5">
                <button
                  className="text-white focus:outline-none absolute right-[20px]"
                  onClick={toggleMenu}
                >
                  {isMenuOpen ? (
                    <Cross2Icon className="w-[32px] h-[25px]" />
                  ) : (
                    <HamburgerMenuIcon className="w-[32px] h-[25px]" />
                  )}
                </button>
              </div>
            ) : null}

            <nav
              className={`lg:flex md:flex flex-wrap mob_nav ${
                isMenuOpen ? "block" : "hidden"
              } lg:w-auto md:w-auto w-full`}
            >
              {!isMobile ? (
                <ul className="nav-menu cmd accounting nav-center desktop_menu">
                  {categories.map((category) => (
                    <li key={category.id} className="relative">
                      <button
                        className={`cms-btn !flex items-center xl:!px-12 p-1 ${
                          activeCategory === category.slug
                            ? "text-[#FFFFFF]"
                            : "text-[#BBBBBB]"
                        }`}
                        onMouseEnter={() => handleCategoryHover(category.slug)}
                        onClick={() => {
                          handleScrollToCategory(category.slug);
                        }}
                      >
                        {category.name}
                      </button>
                      {hoveredCategory === category.slug && (
                        <ul className="absolute top-full left-0 bg-white p-2 hidden h-[400px] overflow-scroll scrollbar-hidden">
                          {pinnedPages.map((pinnedPage, index) => (
                            <li key={index}>
                              <Link
                                href={`/accounting/learn/${category.slug}/${pinnedPage.tagSlug}/${pinnedPage.slug}`}
                                className="px-4 py-2"
                              >
                                {pinnedPage.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                  <li className="!ml-[15px]"></li>
                </ul>
              ) : (
                <ul
                  className={`mobile_menu_accordion ${
                    isMenuOpen ? "block" : "hidden"
                  }`}
                >
                  {categories.map((category) => (
                    <li key={category.id} className="accordion-item">
                      <div className="cms-accordion-label flex justify-between items-center">
                        <button
                          className={`cms-btn text-[#bbbbbb] flex items-center w-full ${
                            activeCategory === category.slug ? "cms-active" : ""
                          }`}
                          onClick={() => handleCategoryClick(category.slug)}
                        >
                          {category.name}
                          <TriangleDownIcon className="ml-2" />
                        </button>
                      </div>
                      {activeCategory === category.slug && (
                        <ul>
                          {(pinnedPagesByCategory[category.slug] || []).map(
                            (pinnedPage, index) => (
                              <li key={index} className="sub-accordion-label ">
                                <Link
                                  href={`/accounting/learn/${category.slug}/${pinnedPage.tagSlug}/${pinnedPage.slug}`}
                                  className="text-[#bbbbbb]"
                                >
                                  <span className="flex items-center">
                                    <ChevronRightIcon className="mr-2" />
                                    {pinnedPage.name}
                                  </span>
                                </Link>
                              </li>
                            ),
                          )}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </nav>

            <nav
              className={`lg:flex md:flex flex-wrap mob_nav ${
                isMenuOpen ? "block" : "hidden"
              } lg:w-auto md:w-auto w-full`}
            >
              {!isMobile ? (
                <ul className="nav-menu accounting nav-center desktop_menu">
                  <li className="!ml-[15px]">
                    <a
                      className="new_btn_2 bg-[#3C86F1] text-white rounded-[20px_20px_20px_0]"
                      href="tel:+919513881313"
                    >
                      <span className="flex items-center gap-2">
                        <Image
                          src="https://ik.imagekit.io/mileseducation/miles_website/accounting/miles-education-cpa-fees-miles-education-cma/Call.webp"
                          alt="Call icon"
                          className="w-5 h-5"
                          width={40}
                          height={40}
                          priority
                        />
                        <span className="flex flex-col items-start pt-2">
                          <span className="text-xs font-normal leading-[0.5]">
                            Talk to an Expert
                          </span>
                          <span className="text-lg font-bold">9513881313</span>
                        </span>
                      </span>
                    </a>
                  </li>
                </ul>
              ) : null}
            </nav>
          </div>
        </div>
      </header>
    </>
  );
};

export default CmsHeader;
